import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';

const Navbar = ({ onCartClick }) => {
  const { itemCount } = useCart();
  const { settings } = useSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Menu', href: '/menu' },
    { label: 'Combos', href: '/combos' },
    { label: 'Reviews', href: '/reviews' },
    { label: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => location.pathname === href;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/menu?search=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  return (
    <nav className="navbar-floating" data-testid="navbar">
      <div className="flex items-center justify-between px-4 md:px-6 py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          {settings.logo ? (
            <img src={settings.logo} alt={settings.bakeryName} className="h-10 w-auto object-contain" />
          ) : (
            <LogoMark />
          )}
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                isActive(link.href)
                  ? 'bg-white/20 text-white'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Search */}
          {searchOpen ? (
            <form onSubmit={handleSearch} className="flex items-center gap-1 animate-fade-in">
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="bg-white/20 text-white placeholder-white/60 text-sm rounded-full px-3 py-1.5 outline-none w-32 md:w-48"
                data-testid="search-input"
              />
              <button type="button" onClick={() => setSearchOpen(false)} className="text-white/70 hover:text-white p-1">
                <X size={16} />
              </button>
            </form>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="search-button"
            >
              <Search size={18} />
            </button>
          )}

          {/* Cart */}
          <button
            onClick={onCartClick}
            className="relative text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="cart-button"
          >
            <ShoppingCart size={18} />
            {itemCount > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-rose-400 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center"
                data-testid="cart-count"
              >
                {itemCount > 9 ? '9+' : itemCount}
              </span>
            )}
          </button>

          {/* Order Now */}
          <Link
            to="/menu"
            className="hidden md:flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
            data-testid="order-now-button"
          >
            <ShoppingBag size={15} />
            Order Now
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-button"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-white/10 px-4 pb-4 pt-2 animate-fade-in">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className={`block py-2.5 px-4 rounded-xl text-sm font-medium transition-colors mb-1 ${
                isActive(link.href) ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/menu"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-2 mt-2 bg-[#7A1125] text-white text-sm font-semibold px-5 py-2.5 rounded-full w-full justify-center"
          >
            <ShoppingBag size={15} />
            Order Now
          </Link>
        </div>
      )}
    </nav>
  );
};

const LogoMark = () => (
  <div className="flex items-center gap-2">
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="18" cy="18" r="17" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1"/>
      <path d="M18 6 C10 10, 8 14, 8 18 C8 24, 12 28, 18 30 C24 28, 28 24, 28 18 C28 14, 26 10, 18 6Z" fill="rgba(255,255,255,0.3)"/>
      <ellipse cx="18" cy="19" rx="6" ry="4" fill="rgba(255,255,255,0.5)"/>
      <circle cx="18" cy="15" r="3" fill="white" opacity="0.8"/>
    </svg>
    <div>
      <div className="font-playfair text-white font-bold text-base leading-none">Apni Dhani</div>
      <div className="text-white/60 text-[9px] uppercase tracking-[0.2em] leading-none">— BAKERY —</div>
    </div>
  </div>
);

export default Navbar;
